// Typed TanStack Query hooks for the user-facing Balance page (`/v1/billing/*`).
//
// This is the user (Mini App) endpoint family — distinct from `/v1/billing/admin/*`
// which is covered by use-billing.ts. The legacy UserBalance.jsx fired six
// fetches in parallel inside one `loadData()`; here we expose them as
// individual queries so React can render partial UI as data arrives.
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import type { PanelAuth } from '@/lib/panel-auth';

// ── Domain types ─────────────────────────────────────────────────────────
export interface BalanceResponse {
  credits: number | string;
  billing_email?: string | null;
  /** Some endpoints include nested feature flags / pricing. Allow extras safely. */
  [key: string]: unknown;
}

export type TransactionKind =
  | 'topup'
  | 'llm_request'
  | 'voice_transcription'
  | 'image_recognition'
  | 'image_generation'
  | 'document_analysis'
  | 'referral_reward'
  | 'referral_registration'
  | 'referral_studio_invite'
  | 'studio_invite_welcome'
  | 'admin_grant'
  | 'admin_fine'
  | 'gift_purchase'
  | 'gift_received'
  | 'gift_refund'
  | string;

export interface Transaction {
  tx_id: string | number;
  kind: TransactionKind;
  credits_delta: number | string;
  description?: string;
  meta?: { chat_title?: string; [key: string]: unknown };
  created_at: string;
}

export interface TransactionsResponse {
  items: Transaction[];
}

export interface UserOrder {
  order_id: string | number;
  status: 'completed' | 'pending' | 'failed' | 'new' | 'canceled' | string;
  credits: number | string;
  amount_rub: number | string;
  created_at: string;
  /** Причина отмены от ЮKassa (meta->cancellation_details->reason), только у canceled. */
  cancel_reason?: string | null;
}

export interface UserOrdersResponse {
  items: UserOrder[];
}

export interface PricingResponse {
  /** Стартовый грант и его срок — из базы (SSOT); отсутствуют, если касса не ответила. */
  welcome_grant?: number;
  welcome_grant_ttl_days?: number;
  credits_per_rub: number;
  rub_per_credit: number | string;
  min_amount_rub?: number;
  max_amount_rub?: number;
  /** Плоские пресеты по ставке (БЕЗ скидок) — backend /billing/pricing их и считает. */
  presets?: { rub: number; credits: number | string }[];
  yookassa_enabled?: boolean;
}

export interface Gift {
  gift_id: string | number;
  status:
    | 'pending_payment'
    | 'pending_delivery'
    | 'delivered'
    | 'expired'
    | 'cancelled'
    | 'chargebacked'
    | string;
  credits: number | string;
  message?: string;
  created_at: string;
  delivered_at?: string;
  redeem_token?: string;
}

export interface GiftsResponse {
  items: Gift[];
}

export interface GiftSearchUser {
  user_id: number | string;
  username?: string;
  first_name?: string;
}

export interface GiftSearchResponse {
  items: GiftSearchUser[];
}

export interface CreatePaymentRequest {
  amount_rub: number;
  email?: string;
  /** Согласие сохранить способ оплаты (галочка) → save_payment_method:true (за гейтом). */
  save_method?: boolean;
}

export interface CreatePaymentResponse {
  confirmation_url?: string;
  /** Заказ, созданный под платёж, — по нему панель следит за подтверждением оплаты. */
  order_id?: number;
  payment_id?: string;
}

export interface CreateGiftRequest {
  amount_rub: number;
  email?: string;
  message?: string;
  recipient_id?: number | string;
}

export interface CancelGiftResponse {
  refunded_credits: number | string;
}

// ── Queries ──────────────────────────────────────────────────────────────
export function useUserBalance(
  token: PanelAuth | null,
  options?: Omit<UseQueryOptions<BalanceResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<BalanceResponse>({
    queryKey: ['billing', 'balance'],
    queryFn: () => apiFetch<BalanceResponse>('/v1/billing/balance', { token }),
    enabled: !!token && (options?.enabled ?? true),
    ...options,
  });
}

export function useUserTransactions(
  token: PanelAuth | null,
  limit = 20,
  options?: Omit<UseQueryOptions<TransactionsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TransactionsResponse>({
    queryKey: ['billing', 'transactions', limit],
    queryFn: () =>
      apiFetch<TransactionsResponse>(`/v1/billing/transactions?limit=${limit}`, { token }),
    enabled: !!token && (options?.enabled ?? true),
    ...options,
  });
}

export function useUserOrders(
  token: PanelAuth | null,
  limit = 10,
  options?: Omit<UseQueryOptions<UserOrdersResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<UserOrdersResponse>({
    queryKey: ['billing', 'orders', limit],
    queryFn: () => apiFetch<UserOrdersResponse>(`/v1/billing/orders?limit=${limit}`, { token }),
    enabled: !!token && (options?.enabled ?? true),
    ...options,
  });
}

export function usePricing(
  token: PanelAuth | null,
  options?: Omit<UseQueryOptions<PricingResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<PricingResponse>({
    queryKey: ['billing', 'pricing'],
    queryFn: () => apiFetch<PricingResponse>('/v1/billing/pricing', { token }),
    enabled: !!token && (options?.enabled ?? true),
    staleTime: 10 * 60_000, // pricing barely changes mid-session.
    ...options,
  });
}

export function useSentGifts(
  token: PanelAuth | null,
  limit = 10,
  options?: Omit<UseQueryOptions<GiftsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<GiftsResponse>({
    queryKey: ['billing', 'gift', 'sent', limit],
    queryFn: async () => {
      try {
        return await apiFetch<GiftsResponse>(`/v1/billing/gift/sent?limit=${limit}`, { token });
      } catch {
        // Treat as empty when the endpoint isn't deployed yet (legacy parity).
        return { items: [] };
      }
    },
    enabled: !!token && (options?.enabled ?? true),
    ...options,
  });
}

export function useReceivedGifts(
  token: PanelAuth | null,
  limit = 10,
  options?: Omit<UseQueryOptions<GiftsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<GiftsResponse>({
    queryKey: ['billing', 'gift', 'received', limit],
    queryFn: async () => {
      try {
        return await apiFetch<GiftsResponse>(`/v1/billing/gift/received?limit=${limit}`, { token });
      } catch {
        return { items: [] };
      }
    },
    enabled: !!token && (options?.enabled ?? true),
    ...options,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────
export function useCreatePayment(token: PanelAuth | null) {
  return useMutation<CreatePaymentResponse, Error, CreatePaymentRequest>({
    mutationFn: (req) =>
      apiFetch<CreatePaymentResponse>('/v1/billing/yookassa/create-payment', {
        method: 'POST',
        token,
        body: JSON.stringify(req),
      }),
  });
}

export function useCreateGift(token: PanelAuth | null) {
  return useMutation<CreatePaymentResponse, Error, CreateGiftRequest>({
    mutationFn: (req) =>
      apiFetch<CreatePaymentResponse>('/v1/billing/gift/create', {
        method: 'POST',
        token,
        body: JSON.stringify(req),
      }),
  });
}

export function useCancelGift(token: PanelAuth | null) {
  const qc = useQueryClient();
  return useMutation<CancelGiftResponse, Error, string | number>({
    mutationFn: (gift_id) =>
      apiFetch<CancelGiftResponse>('/v1/billing/gift/cancel', {
        method: 'POST',
        token,
        body: JSON.stringify({ gift_id }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export async function searchGiftRecipients(
  token: PanelAuth | null,
  query: string
): Promise<GiftSearchResponse> {
  return apiFetch<GiftSearchResponse>(
    `/v1/billing/gift/search-users?q=${encodeURIComponent(query)}`,
    { token }
  );
}
